/**
 * @module ArtefactBrowser.parts
 * @purpose Sub-components for ArtefactBrowser — extracted to satisfy file/function size limits
 */

import { type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Search,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  RefreshCw,
  Check,
  FileCode,
} from 'lucide-react'
import type { VqlArtefact } from '../../types/velociraptor'
import { ArtefactParameterList } from './ArtefactParameterList'

type ParameterChangeHandler =
  | ((artefactName: string, paramName: string, value: string) => void)
  | undefined

interface SelectButtonProps {
  artefactName: string
  isSelected: boolean
  onToggleSelect: (name: string) => void
}

export function SelectButton({
  artefactName,
  isSelected,
  onToggleSelect,
}: SelectButtonProps): ReactElement {
  const { t } = useTranslation('cases')
  const aria = isSelected
    ? t('velociraptor.deselectArtefact', 'Deselect artefact')
    : t('velociraptor.selectArtefact', 'Select artefact')
  return (
    <button
      onClick={() => {
        onToggleSelect(artefactName)
      }}
      className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-colors ${
        isSelected
          ? 'border-[#4e5e82] bg-[#4e5e82]'
          : 'border-slate-300 hover:border-[#8a9bba] dark:border-slate-600'
      }`}
      aria-label={aria}
    >
      {isSelected && <Check className="h-3 w-3 text-white" />}
    </button>
  )
}

interface ExpandButtonProps {
  artefactName: string
  isExpanded: boolean
  onToggleExpand: (name: string) => void
}

export function ExpandButton({
  artefactName,
  isExpanded,
  onToggleExpand,
}: ExpandButtonProps): ReactElement {
  const { t } = useTranslation('cases')
  const aria = isExpanded
    ? t('velociraptor.collapseArtefact', 'Collapse artefact')
    : t('velociraptor.expandArtefact', 'Expand artefact')
  return (
    <button
      onClick={() => {
        onToggleExpand(artefactName)
      }}
      className="flex-shrink-0 p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
      aria-label={aria}
    >
      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
    </button>
  )
}

interface ArtefactRowDetailProps {
  artefact: VqlArtefact
  isExpanded: boolean
  onToggleExpand: (name: string) => void
}

export function ArtefactRowDetail({
  artefact,
  isExpanded,
  onToggleExpand,
}: ArtefactRowDetailProps): ReactElement {
  const hasParams = artefact.parameters.length > 0
  return (
    <div
      className="min-w-0 flex-1 cursor-pointer"
      onClick={() => {
        if (hasParams) onToggleExpand(artefact.name)
      }}
    >
      <div className="flex items-center gap-2">
        <span className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
          {artefact.name}
        </span>
        {hasParams && (
          <span className="inline-flex flex-shrink-0 items-center gap-0.5 text-xs text-slate-400">
            <FileCode className="h-3 w-3" />
            {artefact.parameters.length}
          </span>
        )}
      </div>
      {artefact.description && (
        <p
          className={`mt-0.5 text-xs text-slate-500 dark:text-slate-400 ${isExpanded ? '' : 'truncate'}`}
        >
          {artefact.description}
        </p>
      )}
    </div>
  )
}

interface ArtefactRowProps {
  artefact: VqlArtefact
  isSelected: boolean
  isExpanded: boolean
  parameterOverrides: Record<string, string | number | boolean>
  onToggleSelect: (name: string) => void
  onToggleExpand: (name: string) => void
  onParameterChange?: ParameterChangeHandler
}

export function ArtefactRow({
  artefact,
  isSelected,
  isExpanded,
  parameterOverrides,
  onToggleSelect,
  onToggleExpand,
  onParameterChange,
}: ArtefactRowProps): ReactElement {
  const hasParams = artefact.parameters.length > 0
  const containerClass = `border-b border-slate-100 dark:border-slate-700/50 last:border-b-0 ${
    isSelected ? 'bg-[#4e5e82]/5/50 dark:bg-[#8a9bba]/20/20' : ''
  }`
  return (
    <div className={containerClass}>
      <div className="flex items-center gap-2 px-3 py-2 transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/30">
        <SelectButton
          artefactName={artefact.name}
          isSelected={isSelected}
          onToggleSelect={onToggleSelect}
        />
        {hasParams ? (
          <ExpandButton
            artefactName={artefact.name}
            isExpanded={isExpanded}
            onToggleExpand={onToggleExpand}
          />
        ) : (
          <div className="w-5 flex-shrink-0" />
        )}
        <ArtefactRowDetail
          artefact={artefact}
          isExpanded={isExpanded}
          onToggleExpand={onToggleExpand}
        />
      </div>
      {isExpanded && hasParams && (
        <ArtefactParameterList
          artefactName={artefact.name}
          parameters={artefact.parameters}
          overrides={parameterOverrides}
          onParameterChange={onParameterChange}
        />
      )}
    </div>
  )
}

interface CategoryButtonProps {
  category: string
  isExpanded: boolean
  selectedCount: number
  totalCount: number
  onToggle: (category: string) => void
}

function CategoryButton({
  category,
  isExpanded,
  selectedCount,
  totalCount,
  onToggle,
}: CategoryButtonProps): ReactElement {
  return (
    <button
      onClick={() => {
        onToggle(category)
      }}
      className="flex w-full items-center gap-2 px-2 py-2.5 transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/50"
    >
      {isExpanded ? (
        <ChevronDown className="h-4 w-4 text-slate-400" />
      ) : (
        <ChevronRight className="h-4 w-4 text-slate-400" />
      )}
      <FolderOpen className="h-4 w-4 text-violet-500" />
      <span className="flex-1 text-left text-sm font-medium text-slate-700 dark:text-slate-300">
        {category}
      </span>
      <span className="text-xs text-slate-400 dark:text-slate-500">
        {selectedCount > 0 && (
          <span className="mr-1 text-[#4e5e82] dark:text-[#8a9bba]">{selectedCount}/</span>
        )}
        {totalCount}
      </span>
    </button>
  )
}

interface CategoryListProps {
  sortedCategories: string[]
  artefactsByCategory: Record<string, VqlArtefact[]>
  expandedCategories: Set<string>
  selectedArtefacts: string[]
  parameterOverrides: Record<string, Record<string, string | number | boolean>>
  expandedArtefacts: Set<string>
  onToggleCategory: (category: string) => void
  onToggleSelect: (name: string) => void
  onToggleExpand: (name: string) => void
  onParameterChange?: ParameterChangeHandler
}

export function CategoryList({
  sortedCategories,
  artefactsByCategory,
  expandedCategories,
  selectedArtefacts,
  parameterOverrides,
  expandedArtefacts,
  onToggleCategory,
  onToggleSelect,
  onToggleExpand,
  onParameterChange,
}: CategoryListProps): ReactElement {
  const { t } = useTranslation('cases')
  if (sortedCategories.length === 0) {
    return (
      <div className="py-12 text-center text-slate-500 dark:text-slate-400">
        {t('velociraptor.noArtefacts')}
      </div>
    )
  }
  return (
    <div className="space-y-1">
      {sortedCategories.map((category) => {
        const categoryArtefacts = artefactsByCategory[category] ?? []
        const isExpanded = expandedCategories.has(category)
        const selectedInCategory = categoryArtefacts.filter((art) =>
          selectedArtefacts.includes(art.name)
        ).length
        return (
          <div key={category} className="border-b border-slate-100 dark:border-slate-700/50">
            <CategoryButton
              category={category}
              isExpanded={isExpanded}
              selectedCount={selectedInCategory}
              totalCount={categoryArtefacts.length}
              onToggle={onToggleCategory}
            />
            {isExpanded && (
              <div className="ml-6 border-l-2 border-slate-100 dark:border-slate-700">
                {categoryArtefacts.map((artefact) => (
                  <ArtefactRow
                    key={artefact.name}
                    artefact={artefact}
                    isSelected={selectedArtefacts.includes(artefact.name)}
                    isExpanded={expandedArtefacts.has(artefact.name)}
                    parameterOverrides={parameterOverrides[artefact.name] ?? {}}
                    onToggleSelect={onToggleSelect}
                    onToggleExpand={onToggleExpand}
                    onParameterChange={onParameterChange}
                  />
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

interface BrowserToolbarProps {
  searchTerm: string
  onSearchChange: (value: string) => void
  selectedCount: number
  onExpandAll: () => void
  onCollapseAll: () => void
  loading: boolean
  onRefresh?: (() => void) | undefined
}

export function BrowserToolbar({
  searchTerm,
  onSearchChange,
  selectedCount,
  onExpandAll,
  onCollapseAll,
  loading,
  onRefresh,
}: BrowserToolbarProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="mb-4 flex-shrink-0 space-y-3">
      <div className="relative">
        <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder={t('velociraptor.searchArtefacts')}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value)
          }}
          className="w-full rounded-lg border border-slate-200 bg-white py-2 pr-4 pl-10 text-sm text-slate-900 placeholder-slate-400 focus:border-transparent focus:ring-2 focus:ring-[#4e5e82] focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
        />
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs">
          <button
            onClick={onExpandAll}
            className="text-slate-500 hover:text-[#4e5e82] dark:text-slate-400 dark:hover:text-[#8a9bba]"
          >
            {t('velociraptor.expandAll')}
          </button>
          <span className="text-slate-300 dark:text-slate-600">|</span>
          <button
            onClick={onCollapseAll}
            className="text-slate-500 hover:text-[#4e5e82] dark:text-slate-400 dark:hover:text-[#8a9bba]"
          >
            {t('velociraptor.collapseAll')}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {t('velociraptor.selectedCount', { count: selectedCount })}
          </span>
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={loading}
              className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50 dark:hover:bg-slate-700 dark:hover:text-slate-300"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

interface SearchResultsProps {
  filteredArtefacts: VqlArtefact[]
  searchTerm: string
  selectedArtefacts: string[]
  expandedArtefacts: Set<string>
  parameterOverrides: Record<string, Record<string, string | number | boolean>>
  onToggleSelect: (name: string) => void
  onToggleExpand: (name: string) => void
  onParameterChange?: ParameterChangeHandler
}

export function SearchResults({
  filteredArtefacts,
  searchTerm,
  selectedArtefacts,
  expandedArtefacts,
  parameterOverrides,
  onToggleSelect,
  onToggleExpand,
  onParameterChange,
}: SearchResultsProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="space-y-0">
      <p className="mb-3 px-3 text-xs text-slate-500 dark:text-slate-400">
        {t('velociraptor.searchResults', { count: filteredArtefacts.length, term: searchTerm })}
      </p>
      <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
        {filteredArtefacts.map((artefact) => (
          <ArtefactRow
            key={artefact.name}
            artefact={artefact}
            isSelected={selectedArtefacts.includes(artefact.name)}
            isExpanded={expandedArtefacts.has(artefact.name)}
            parameterOverrides={parameterOverrides[artefact.name] ?? {}}
            onToggleSelect={onToggleSelect}
            onToggleExpand={onToggleExpand}
            onParameterChange={onParameterChange}
          />
        ))}
      </div>
      {filteredArtefacts.length === 0 && (
        <div className="py-8 text-center text-slate-500 dark:text-slate-400">
          {t('velociraptor.noMatches')}
        </div>
      )}
    </div>
  )
}

interface BrowserBodyProps {
  loading: boolean
  searchTerm: string
  filteredArtefacts: VqlArtefact[] | null
  sortedCategories: string[]
  artefactsByCategory: Record<string, VqlArtefact[]>
  expandedCategories: Set<string>
  selectedArtefacts: string[]
  expandedArtefacts: Set<string>
  parameterOverrides: Record<string, Record<string, string | number | boolean>>
  onToggleCategory: (category: string) => void
  onToggleSelect: (name: string) => void
  onToggleExpand: (name: string) => void
  onParameterChange?: ParameterChangeHandler
}

export function BrowserBody(props: BrowserBodyProps): ReactElement {
  const { loading, searchTerm, filteredArtefacts } = props
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" role="status">
        <RefreshCw className="h-6 w-6 animate-spin text-[#4e5e82]" />
      </div>
    )
  }
  if (searchTerm && filteredArtefacts) {
    return (
      <SearchResults
        filteredArtefacts={filteredArtefacts}
        searchTerm={searchTerm}
        selectedArtefacts={props.selectedArtefacts}
        expandedArtefacts={props.expandedArtefacts}
        parameterOverrides={props.parameterOverrides}
        onToggleSelect={props.onToggleSelect}
        onToggleExpand={props.onToggleExpand}
        onParameterChange={props.onParameterChange}
      />
    )
  }
  return (
    <CategoryList
      sortedCategories={props.sortedCategories}
      artefactsByCategory={props.artefactsByCategory}
      expandedCategories={props.expandedCategories}
      selectedArtefacts={props.selectedArtefacts}
      parameterOverrides={props.parameterOverrides}
      expandedArtefacts={props.expandedArtefacts}
      onToggleCategory={props.onToggleCategory}
      onToggleSelect={props.onToggleSelect}
      onToggleExpand={props.onToggleExpand}
      onParameterChange={props.onParameterChange}
    />
  )
}

